import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'

import { useI18N } from '@susu/i18n/client'
import {
  Button,
  Checkbox,
  Icon,
  InputField,
  InputPhoneNumber,
  Label,
  Loader,
  SelectField,
} from '@susu/ui/components'

import avo from '../avo/avo'
import { ROLE, useCustomerHub } from '../contexts/CustomerHubContext'
import { useModal } from '../contexts/ModalContext'
import { useAddresses } from '../hooks/useAddresses'
import { useSkeletonPhase } from '../hooks/useSkeletonPhase'
import {
  clearFieldError,
  countryUsesDistrict,
  EMPTY_ADDRESS,
  formatAddressLineEntries,
  MAX_ADDRESSES,
  toAddressPayload,
  validateAddress,
  type Address,
} from '../lib/address'
import { COUNTRY_OPTIONS } from '../lib/countries'
import {
  countryUsesStateSelect,
  getSubdivisionOptionsForCountry,
} from '../lib/subdivisions'
import { cn } from '../lib/utils'

import AddressListSkeleton from './AddressListSkeleton'

import type { EnvironmentWithChina } from '../App'
import type { AddressFieldsValueType } from '../avo/avo'
import type { CustomerData } from '../types/customer'

interface AddressBookModalProps {
  customerData: CustomerData
  env: EnvironmentWithChina
  authToken?: string
}

type ViewMode = 'list' | 'form'

const LISTBOX_MAX_HEIGHT_PX = 188

function useListboxOpenUpward(wrapperRef: RefObject<HTMLDivElement | null>) {
  const [openUpward, setOpenUpward] = useState(false)

  const updateDirection = useCallback(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const trigger = wrapper.querySelector(
      "[role='combobox']",
    ) as HTMLElement | null
    const scrollContainer = wrapper.closest('form') as HTMLElement | null
    if (!trigger || !scrollContainer) return

    const triggerRect = trigger.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    const spaceBelow = containerRect.bottom - triggerRect.bottom
    const spaceAbove = triggerRect.top - containerRect.top

    setOpenUpward(spaceBelow < LISTBOX_MAX_HEIGHT_PX && spaceAbove > spaceBelow)
  }, [wrapperRef])

  useEffect(() => {
    window.addEventListener('resize', updateDirection)
    return () => {
      window.removeEventListener('resize', updateDirection)
    }
  }, [updateDirection])

  return { openUpward, updateDirection }
}

// Address Card Component - matches Figma "Action Card" design
interface AddressCardProps {
  address: Address
  onEdit: () => void
}

const AddressCard = memo(function AddressCard({
  address,
  onEdit,
}: AddressCardProps) {
  const { t } = useI18N()
  const entries = formatAddressLineEntries(address)
  const [nameEntry, ...detailEntries] = entries

  return (
    <button
      onClick={onEdit}
      className="flex w-full items-center rounded-lg border border-border p-20 text-left transition-all hover:border-border-hover active:scale-[0.98] active:bg-surface-hover"
      type="button"
      data-testid="button-edit-address"
    >
      <span className="flex flex-1 flex-col gap-2 pb-2 pr-20">
        <span className="flex items-center gap-8">
          <span
            className="text-base font-medium leading-base text-text"
            data-testid="address-card-name"
          >
            {nameEntry?.text}
          </span>
          {address.isDefault ? (
            <Label
              size="s"
              color="blue"
              text={t('addressBook.primary')}
              data-testid="address-card-primary-label"
            />
          ) : null}
        </span>
        <span className="flex flex-col text-sm font-light leading-sm text-text">
          {detailEntries.map(({ key, text }) => (
            <span key={key} data-testid={`address-card-${key}`}>
              {text}
            </span>
          ))}
        </span>
      </span>
      <Icon className="-mr-2" name="chevron-right" size={24} color="text" />
    </button>
  )
})

// The list view: one card per address, plus the "Add address" card until the
// account hits `MAX_ADDRESSES`.
interface AddressListProps {
  addresses: Address[]
  isLoading: boolean
  isError: boolean
  /** `undefined` opens a blank New address form. */
  onOpenAddress: (address: Address | undefined) => void
}

function AddressList({
  addresses,
  isLoading,
  isError,
  onOpenAddress,
}: AddressListProps) {
  const { t } = useI18N()
  const { phase, contentClassName } = useSkeletonPhase(isLoading)

  // Exact-layout placeholders instead of a centred bar (RP-5936): the list
  // keeps its shape from the first frame, `reserve` holds it without painting
  // so a cached response never flashes, and a shown skeleton keeps its
  // minimum visible time.
  if (phase !== 'content') {
    return <AddressListSkeleton isReserved={phase === 'reserve'} />
  }

  // A read failure must never look like an empty address book — the whole
  // sub-resource answers 404 while the backend kill switch is off.
  if (isError) {
    return (
      <div
        className="rounded-md bg-surface-error p-12"
        data-testid="address-list-error"
      >
        <p className="text-sm text-text-error">
          {t('addressBook.failedToLoad')}
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn('flex flex-col gap-12', contentClassName)}
      data-testid="address-list"
    >
      {addresses.map((address) => (
        <AddressCard
          key={address.addressId}
          address={address}
          onEdit={() => onOpenAddress(address)}
        />
      ))}

      {addresses.length < MAX_ADDRESSES ? (
        <button
          onClick={() => onOpenAddress(undefined)}
          className="flex w-full items-center rounded-lg border border-border p-20 text-left transition-all hover:border-border-hover active:scale-[0.98] active:bg-surface-hover"
          type="button"
          data-testid="button-add-address"
        >
          <span className="flex-1 text-base font-medium leading-base text-text">
            {t('addressBook.addAddress')}
          </span>
          <Icon className="-mr-2" name="plus" size={24} color="text" />
        </button>
      ) : null}
    </div>
  )
}

// Centred confirmation over the panel — deleting always goes through it.
interface DeleteConfirmDialogProps {
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

function DeleteConfirmDialog({
  isDeleting,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  const { t } = useI18N()

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black-alpha-300 px-40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="address-delete-confirm-title"
    >
      <div className="flex w-full max-w-300 flex-col gap-16 rounded-lg bg-surface p-20 text-center">
        <h2
          id="address-delete-confirm-title"
          className="text-base font-medium leading-base text-text"
        >
          {t('addressBook.deleteConfirmTitle')}
        </h2>
        <p className="text-sm font-light leading-sm text-text-secondary">
          {t('addressBook.deleteConfirmBody')}
        </p>
        <Button
          variant="filled"
          size="l"
          onClick={!isDeleting ? onConfirm : undefined}
          aria-disabled={isDeleting}
          className={`w-full ${isDeleting ? 'pointer-events-none opacity-50' : ''}`}
          data-testid="button-confirm-delete-address"
        >
          {isDeleting ? (
            <Loader
              ariaLabel={t('addressBook.deletingAddress')}
              className="h-auto"
            />
          ) : (
            t('addressBook.deleteAddress')
          )}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-text underline"
          data-testid="button-keep-address"
        >
          {t('addressBook.keepAddress')}
        </button>
      </div>
    </div>
  )
}

// Address Form Component - Single column layout matching Figma
interface AddressFormProps {
  address: Address
  onChange: (name: string) => React.ChangeEventHandler<HTMLInputElement>
  onFieldValue: (name: keyof Address, value: string) => void
  onPrimaryChange: (checked: boolean) => void
  /**
   * Locks the checkbox on (checked + disabled) when clearing it would leave
   * the account with no primary address: the current primary can't un-set
   * itself, and the very first address has nothing to hand the flag to.
   */
  lockPrimary: boolean
  errors: Record<string, string>
}

const AddressForm = memo(function AddressForm({
  address,
  onChange,
  onFieldValue,
  onPrimaryChange,
  lockPrimary,
  errors,
}: AddressFormProps) {
  const { t } = useI18N()
  const countrySelectWrapperRef = useRef<HTMLDivElement>(null)
  const stateSelectWrapperRef = useRef<HTMLDivElement>(null)
  const phoneWrapperRef = useRef<HTMLDivElement>(null)
  const [activeDropdown, setActiveDropdown] = useState<
    'country' | 'state' | 'phone' | null
  >(null)
  const {
    openUpward: openCountryDropdownUpward,
    updateDirection: updateCountryDropdownDirection,
  } = useListboxOpenUpward(countrySelectWrapperRef)
  const {
    openUpward: openStateDropdownUpward,
    updateDirection: updateStateDropdownDirection,
  } = useListboxOpenUpward(stateSelectWrapperRef)

  const showStateSelect = countryUsesStateSelect(address.countryCode)
  const showDistrict = countryUsesDistrict(address.countryCode)
  const stateOptions = useMemo(
    () => getSubdivisionOptionsForCountry(address.countryCode),
    [address.countryCode],
  )

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const insideCountry = countrySelectWrapperRef.current?.contains(target)
      const insideState = stateSelectWrapperRef.current?.contains(target)
      const insidePhone = phoneWrapperRef.current?.contains(target)
      if (!insideCountry && !insideState && !insidePhone) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [])

  return (
    <div className="flex flex-col gap-20 pt-40 pb-60">
      <InputField
        id="address-firstName"
        label={t('addressBook.form.firstName')}
        name="firstName"
        type="text"
        value={address.firstName}
        onChange={onChange('firstName')}
        error={errors.firstName}
        data-testid="address-first-name-input"
      />
      <InputField
        id="address-lastName"
        label={t('addressBook.form.lastName')}
        name="lastName"
        type="text"
        value={address.lastName}
        onChange={onChange('lastName')}
        error={errors.lastName}
        data-testid="address-last-name-input"
      />
      <InputField
        id="address-company"
        label={t('addressBook.form.company')}
        name="company"
        type="text"
        value={address.company}
        onChange={onChange('company')}
        error={errors.company}
        data-testid="address-company-input"
      />
      <InputField
        id="address-addressLine1"
        label={t('addressBook.form.addressLine1')}
        name="addressLine1"
        type="text"
        value={address.addressLine1}
        onChange={onChange('addressLine1')}
        error={errors.addressLine1}
        data-testid="address-street-input"
      />
      <InputField
        id="address-addressLine2"
        label={t('addressBook.form.addressLine2')}
        name="addressLine2"
        type="text"
        value={address.addressLine2}
        onChange={onChange('addressLine2')}
        error={errors.addressLine2}
        data-testid="address-number-input"
      />
      <InputField
        id="address-postalCode"
        label={t('addressBook.form.postcode')}
        name="postalCode"
        type="text"
        value={address.postalCode}
        onChange={onChange('postalCode')}
        error={errors.postalCode}
        data-testid="address-postcode-input"
      />
      <InputField
        id="address-city"
        label={t('addressBook.form.city')}
        name="city"
        type="text"
        value={address.city}
        onChange={onChange('city')}
        error={errors.city}
        data-testid="address-city-input"
      />
      <div
        ref={countrySelectWrapperRef}
        className={`relative ${
          activeDropdown === 'country' ? 'z-[140]' : 'z-[120]'
        } ${
          openCountryDropdownUpward
            ? "[&_[role='listbox']]:top-auto [&_[role='listbox']]:bottom-44"
            : ''
        }`}
        onClick={() => {
          setActiveDropdown('country')
          updateCountryDropdownDirection()
        }}
        onKeyUp={() => {
          setActiveDropdown('country')
          updateCountryDropdownDirection()
        }}
      >
        <SelectField
          id="address-country"
          label={t('addressBook.form.country')}
          name="countryCode"
          value={address.countryCode}
          options={COUNTRY_OPTIONS}
          onChange={(value) => onFieldValue('countryCode', value)}
          error={errors.countryCode}
          placeholder={t('addressBook.form.selectCountry')}
          data-testid="address-country-select"
        />
      </div>
      {showStateSelect ? (
        <div
          ref={stateSelectWrapperRef}
          className={`relative ${
            activeDropdown === 'state' ? 'z-[140]' : 'z-[120]'
          } ${
            openStateDropdownUpward
              ? "[&_[role='listbox']]:top-auto [&_[role='listbox']]:bottom-44"
              : ''
          }`}
          onClick={() => {
            setActiveDropdown('state')
            updateStateDropdownDirection()
          }}
          onKeyUp={() => {
            setActiveDropdown('state')
            updateStateDropdownDirection()
          }}
        >
          <SelectField
            id="address-state"
            label={t('addressBook.form.state')}
            name="state"
            value={address.state}
            options={stateOptions}
            onChange={(value) => onFieldValue('state', value)}
            error={errors.state}
            placeholder={
              address.countryCode.trim().toUpperCase() === 'CA'
                ? t('addressBook.form.selectProvince')
                : t('addressBook.form.selectState')
            }
            data-testid="address-state-select"
          />
        </div>
      ) : null}
      {showDistrict ? (
        <InputField
          id="address-district"
          label={t('addressBook.form.district')}
          name="district"
          type="text"
          value={address.district}
          onChange={onChange('district')}
          error={errors.district}
          data-testid="address-district-input"
        />
      ) : null}
      {/* `InputPhoneNumber` carries its own hidden label and the dial-code
          selector Figma shows; it takes no visible label, matching the design.

          The wrapper joins the same z-index scheme as the country / state
          selects: the country list has no z-index of its own, so without a
          raised wrapper the select above — which sits at `z-[120]` — paints
          over it whichever way round the DOM order runs. */}
      <div
        ref={phoneWrapperRef}
        className={`relative ${activeDropdown === 'phone' ? 'z-[140]' : 'z-[120]'}`}
        onClick={() => setActiveDropdown('phone')}
        onKeyUp={() => setActiveDropdown('phone')}
        data-testid="address-phone-field"
      >
        <InputPhoneNumber
          id="address-phone"
          name="phone"
          value={address.phone}
          countryCode={address.countryCode.trim().toUpperCase() || undefined}
          onChange={onChange('phone')}
          onFullNumberChange={(fullNumber) => {
            if (fullNumber) onFieldValue('phone', fullNumber)
          }}
          error={errors.phone}
          data-testid="address-phone-input"
        />
      </div>
      <Checkbox
        name="isDefault"
        label={t('addressBook.setAsPrimary')}
        checked={address.isDefault}
        disabled={lockPrimary}
        onChange={(e) => onPrimaryChange(e.target.checked)}
        data-testid="address-primary-checkbox"
      />
    </div>
  )
})

/**
 * AddressBookModal Component
 *
 * Address Book V2 (RP-5872): the full list of a customer's addresses, backed by
 * the Customer API address sub-resource, with add / edit / delete and a single
 * primary address.
 *
 * Figma: https://www.figma.com/design/9Ty1MTdWC6cRu6i9DG3Sjw/-Con--Customer-Hub---Next-Best-Action--Product-ID---Address?node-id=2177-6763
 */
export default function AddressBookModal({
  customerData,
  env,
  authToken,
}: AddressBookModalProps) {
  const { t } = useI18N()
  const { setTitle, setOnBack } = useModal()
  const { role } = useCustomerHub()

  const { list, save, remove } = useAddresses({
    accountNumber: customerData.accountNumber,
    env,
    authToken,
  })
  const addresses = useMemo(() => list.data ?? [], [list.data])

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [editingId, setEditingId] = useState<string | undefined>(undefined)
  const [formData, setFormData] = useState<Address>(EMPTY_ADDRESS)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  /**
   * Bumped on every open so `AddressForm` remounts.
   *
   * The form is never unmounted between opens — it stays rendered and slides
   * out of view — and `InputPhoneNumber` is uncontrolled: intl-tel-input owns
   * its DOM value and ignores an emptied `value` prop. Without a fresh mount
   * the phone field keeps the last address's number while `formData.phone` is
   * empty, so a new address looks filled in but fails the required check.
   */
  const [formInstance, setFormInstance] = useState(0)

  const getUserType = () => {
    if (role === ROLE.CUSTOMER) return [avo.UserType.CUSTOMER]
    if (role === ROLE.STYLE_ADVISOR) return [avo.UserType.STYLE_ADVISOR]
    return []
  }

  const handleBack = useCallback(() => {
    setOnBack(undefined)
    setTitle(t('modals.addressBook'))
    setViewMode('list')
    setEditingId(undefined)
    setErrors({})
    setIsConfirmingDelete(false)
  }, [setOnBack, setTitle, t])

  const openForm = useCallback(
    (address: Address | undefined) => {
      setOnBack(handleBack)
      setTitle(
        address ? t('addressBook.editAddress') : t('addressBook.addAddress'),
      )
      setEditingId(address?.addressId)
      setFormData(
        address ?? {
          ...EMPTY_ADDRESS,
          firstName: customerData.firstName || '',
          lastName: customerData.lastName || '',
          // The very first address on an account is the primary one by default.
          isDefault: addresses.length === 0,
        },
      )
      setErrors({})
      setFormInstance((instance) => instance + 1)
      setViewMode('form')
    },
    [addresses.length, customerData, handleBack, setOnBack, setTitle, t],
  )

  const handleSave = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()

      const validationErrors = validateAddress(formData, t)
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        return
      }

      setErrors({})

      try {
        await save.mutateAsync({
          addressId: editingId,
          address: toAddressPayload(formData),
        })

        const addressFields: AddressFieldsValueType[] = [
          formData.addressLine1 && avo.AddressFields.ADDRESS_LINE_1,
          formData.postalCode && avo.AddressFields.ZIP_CODE,
          formData.city && avo.AddressFields.CITY,
          formData.countryCode && avo.AddressFields.COUNTRY,
        ].filter(Boolean) as AddressFieldsValueType[]

        avo.customerAddressEdited({
          eventLabel: null,
          eventCategory: null,
          eventLocation: 'customer_hub_settings',
          addressType: avo.AddressType.SHIPPING,
          addressFields,
          userType: getUserType(),
        })

        handleBack()
      } catch {
        setErrors({ form: t('addressBook.failedToSave') })
      }
    },
    [formData, editingId, save, handleBack, t],
  )

  const handleDelete = useCallback(async () => {
    if (!editingId) return

    try {
      await remove.mutateAsync(editingId)
      setIsConfirmingDelete(false)
      handleBack()
    } catch {
      setIsConfirmingDelete(false)
      setErrors({ form: t('addressBook.failedToDelete') })
    }
  }, [editingId, remove, handleBack, t])

  const handleFieldChange = useCallback(
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, [name]: value }))
      setErrors((prev) => clearFieldError(prev, name))
    },
    [],
  )

  // Direct value setter for inputs that report a value rather than an event
  // (the selects, and the phone field's resolved international number).
  const handleFieldValue = useCallback((name: keyof Address, value: string) => {
    setFormData((prev) => {
      if (name !== 'countryCode') return { ...prev, [name]: value }

      // Changing country invalidates a state that belongs to the old one.
      const next = { ...prev, countryCode: value }
      const options = getSubdivisionOptionsForCountry(value)
      if (!options.some((o) => o.value === prev.state)) next.state = ''
      return next
    })
    setErrors((prev) =>
      name === 'countryCode'
        ? clearFieldError(clearFieldError(prev, 'countryCode'), 'state')
        : clearFieldError(prev, name),
    )
  }, [])

  const handlePrimaryChange = useCallback((checked: boolean) => {
    setFormData((prev) => ({ ...prev, isDefault: checked }))
  }, [])

  const isListView = viewMode === 'list'
  const isFormView = viewMode === 'form'
  const isEditing = Boolean(editingId)
  const isOnlyAddress = addresses.length <= 1
  // A brand-new first address must be the primary one — SFSC's "exactly one
  // per account" invariant has nowhere else to live, and checkout reads it.
  const isFirstAddress = !isEditing && addresses.length === 0
  const isBusy = save.isPending || remove.isPending

  return (
    <div className="flex h-full flex-col pt-20">
      <div
        className={`relative w-full flex-1 ${
          isFormView ? 'z-20 overflow-visible' : 'overflow-hidden'
        }`}
      >
        {/* List Content */}
        <div
          className={`absolute inset-0 overflow-y-auto px-16 pt-20 pb-60 transition-transform lg:px-20 3xl:px-120 ${
            isListView ? 'translate-x-0' : '-translate-x-full'
          }`}
          aria-hidden={!isListView}
        >
          <AddressList
            addresses={addresses}
            isLoading={list.isLoading}
            isError={list.isError}
            onOpenAddress={openForm}
          />
        </div>

        {/* Form Content */}
        <form
          className={`absolute inset-0 flex flex-col overflow-y-auto px-16 transition-transform lg:px-20 3xl:px-120 ${
            isFormView ? 'z-20 translate-x-0' : 'translate-x-full'
          }`}
          aria-hidden={!isFormView}
          onSubmit={handleSave}
        >
          {errors.form ? (
            <div
              className="mt-16 rounded-md bg-surface-error p-12"
              data-testid="address-form-error"
            >
              <p className="text-sm text-text-error">{errors.form}</p>
            </div>
          ) : null}

          <AddressForm
            key={formInstance}
            address={formData}
            onChange={handleFieldChange}
            onFieldValue={handleFieldValue}
            onPrimaryChange={handlePrimaryChange}
            lockPrimary={(isEditing && formData.isDefault) || isFirstAddress}
            errors={errors}
          />

          {/* The form above ends in `pb-60`, so the pull-up is what sets the
              real gap below the primary checkbox: 60 − 12 = 48px. */}
          {isEditing ? (
            <div className="-mt-12 pb-40 text-center">
              {isOnlyAddress ? (
                <p
                  className="text-sm font-light leading-sm text-text-secondary"
                  data-testid="address-only-address-message"
                >
                  {t('addressBook.onlyAddress')}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsConfirmingDelete(true)}
                  className="inline-flex items-center gap-8 text-sm text-text-error underline"
                  data-testid="button-delete-address"
                >
                  <Icon name="trash" size={20} color="error" />
                  {t('addressBook.deleteAddress')}
                </button>
              )}
            </div>
          ) : null}

          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </div>

      {isFormView && (
        <div className="border-t border-border p-16 lg:p-20">
          <Button
            variant="filled"
            size="l"
            onClick={!isBusy ? handleSave : undefined}
            aria-disabled={isBusy}
            className={`w-full ${isBusy ? 'pointer-events-none opacity-50' : ''}`}
            data-testid="address-form-submit-button"
          >
            {save.isPending ? (
              <Loader
                ariaLabel={t('addressBook.savingAddress')}
                className="h-auto"
              />
            ) : (
              t('addressBook.saveAddress')
            )}
          </Button>
        </div>
      )}

      {isConfirmingDelete ? (
        <DeleteConfirmDialog
          isDeleting={remove.isPending}
          onConfirm={handleDelete}
          onCancel={() => setIsConfirmingDelete(false)}
        />
      ) : null}
    </div>
  )
}
